/**
 * Column definitions for OrdersTanStack.
 * Each def maps a TanStack column to the API sort_key and filter param names.
 *
 * filterType: 'choice' | 'date' | 'text' | null
 * apiSortKey: string sent as sort_key to /api/orders
 * apiFilterKey: string param name for /api/orders (single-value or repeatable)
 * sticky: 'checkbox' | 'order' | false
 */

export const COLUMN_DEFS = [
  {
    id: 'checkbox',
    label: 'Checkbox',
    sortable: false,
    filterType: null,
    sticky: 'checkbox',
    apiSortKey: null,
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'name',
    label: 'Order #',
    sortable: true,
    filterType: null,   // top-bar search handles this
    sticky: 'order',
    apiSortKey: 'name',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'created_at',
    label: 'Created',
    sortable: true,
    filterType: 'date',
    sticky: false,
    apiSortKey: 'created_at',
    apiFilterKey: 'date',   // maps to date_from / date_to params
    defaultHidden: false,
  },
  {
    id: 'customer',
    label: 'Customer',
    sortable: true,
    filterType: null,   // Phase 2
    sticky: false,
    apiSortKey: 'customer',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'customer_email',
    label: 'Email',
    sortable: true,
    filterType: null,   // Not filterable per product decision 2026-08-19 —
                        // email is searchable via the "Search by customer
                        // or order number" box but doesn't get its own
                        // filter chip.
    sticky: false,
    apiSortKey: 'customer_email',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'status',
    apiFilterKey: 'statuses',
    choices: [
      { label: 'Unfulfilled',        value: 'UNFULFILLED' },
      { label: 'Partially Fulfilled',value: 'PARTIALLY_FULFILLED' },
      { label: 'Fulfilled',          value: 'FULFILLED' },
      { label: 'In Progress',        value: 'IN_PROGRESS' },
      { label: 'On Hold',            value: 'ON_HOLD' },
      { label: 'Cancelled',          value: 'CANCELLED' },
    ],
    defaultHidden: false,
  },
  {
    // Shopify Protect coverage snapshotted at cache-write time from
    // order.shopifyProtect.status. Renders as a colored chip:
    //   ACTIVE        → light-green (SUCCESS_SUBDUED)  — will be covered
    //                                                    once fulfilled on
    //                                                    time
    //   PROTECTED     → green       (SUCCESS)          — shipped in time,
    //                                                    now covered
    //   NOT_PROTECTED → orange      (WARNING)          — SLA missed / no
    //                                                    longer covered
    //   PENDING       → yellow      (INFO)             — Shopify still
    //                                                    evaluating
    //   INACTIVE      → red         (CRITICAL)         — coverage disabled
    // Filter is order-wide (not per-loc); NULL rows (shops without the app)
    // are excluded when any value is selected.
    id: 'protect_status',
    label: 'Shopify Protect',
    // AdvancedFilters left-nav uses this shorter label; the column
    // header + Columns picker keep the full "Shopify Protect" name.
    filterLabel: 'Protect Status',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'protect_status',
    apiFilterKey: 'protect_statuses',
    choices: [
      { label: 'Active',        value: 'ACTIVE' },
      { label: 'Protected',     value: 'PROTECTED' },
      { label: 'Not Protected', value: 'NOT_PROTECTED' },
      { label: 'Pending',       value: 'PENDING' },
      { label: 'Inactive',      value: 'INACTIVE' },
    ],
    defaultHidden: false,
  },
  {
    // Shopify order-risk assessment (LOW/MEDIUM/HIGH/PENDING) snapshotted
    // from order.risk.assessments[]. Same filter/sort/render pattern as
    // Shopify Protect above. Renders as a colored chip:
    //   LOW     → green   (SUCCESS)     — safe to fulfill
    //   MEDIUM  → yellow  (WARNING)     — review recommended
    //   HIGH    → red     (CRITICAL)    — likely chargeback risk
    //   PENDING → grey    (INFO)        — Shopify still scoring
    id: 'risk_level',
    label: 'Order Risk',
    filterLabel: 'Order Risk',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'risk_level',
    apiFilterKey: 'risk_levels',
    choices: [
      { label: 'Low',     value: 'LOW' },
      { label: 'Medium',  value: 'MEDIUM' },
      { label: 'High',    value: 'HIGH' },
      { label: 'Pending', value: 'PENDING' },
      { label: 'None',    value: 'NONE' },
    ],
    defaultHidden: false,
  },
  {
    id: 'pick_pct',
    label: 'Pick Status',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'pick_status',
    apiFilterKey: 'pick_statuses',
    choices: [
      { label: 'Not started', value: 'not_started' },
      { label: 'In progress', value: 'in_progress' },
      { label: 'Complete',    value: 'complete' },
    ],
    defaultHidden: false,
  },
  {
    id: 'press_pct',
    label: 'Press Status',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'press_status',
    apiFilterKey: 'press_statuses',
    choices: [
      { label: 'Not started', value: 'not_started' },
      { label: 'In progress', value: 'in_progress' },
      { label: 'Complete',    value: 'complete' },
    ],
    defaultHidden: false,
  },
  {
    id: 'pack_pct',
    label: 'Pack Status',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'pack_status',
    apiFilterKey: 'pack_statuses',
    choices: [
      { label: 'Not started', value: 'not_started' },
      { label: 'In progress', value: 'in_progress' },
      { label: 'Complete',    value: 'complete' },
    ],
    defaultHidden: false,
  },
  {
    id: 'print_status',
    label: 'Print Status',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'print_status',
    apiFilterKey: 'printeds',
    choices: [
      { label: 'Printed',     value: 'printed' },
      { label: 'Not printed', value: 'not_printed' },
    ],
    defaultHidden: false,
  },
  {
    id: 'items',
    label: 'Items',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'items',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'players',
    label: 'Player Count',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'players',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'fulfill_by',
    label: 'Fulfill By',
    sortable: true,
    filterType: 'date',
    sticky: false,
    apiSortKey: 'fulfill_by',
    apiFilterKey: 'fulfill_by',
    defaultHidden: false,
  },
  {
    // The order's `_soccer_fulfillment_location` cache field — useful when admin
    // is at All Locations to see which warehouse the order was assigned to.
    id: 'location',
    label: 'Location',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'location',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'delivery',
    label: 'Delivery',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'delivery',
    // Synthetic key — the OrdersTanStack `filterGetSet[delivery_combined]`
    // splits the selection into delivery_methods + pickup_stores so the
    // header filter can offer "Shipping" alongside the actual pickup-store
    // names (Milwaukee, Long Island, …) instead of a generic "Pickup".
    apiFilterKey: 'delivery_combined',
    choices: [],   // populated dynamically via dynamicChoices.delivery
    // 2026-07-28: enable the search box on the value picker (same UI the
    // Club filter has). The delivery choice list is 100+ entries on
    // soccerpost (every ship-method name + pickup store) so a text filter
    // is needed to find e.g. "Bulk Club Delivery" quickly. AdvancedFilters
    // already carries a `searchValue` on delivery's JSX labels for
    // substring matching (AdvancedFilters.jsx:167) — nothing else needs
    // to change to make it filterable.
    searchable: true,
    defaultHidden: false,
  },
  // Pickup Store column removed 2026-04-30 — the Delivery column already
  // renders the pickup store name on pickup orders, so this duplicated data.
  // The pickup_stores URL filter still works for legacy URLs; it just isn't
  // surfaced as a column anymore.
  {
    id: 'club',
    label: 'Club',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'club',
    apiFilterKey: 'clubs',
    choices: [],         // populated from /api/orders/filter-options
    searchable: true,
    defaultHidden: false,
  },
  {
    id: 'teams',
    label: 'Team(s)',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'teams',
    apiFilterKey: 'teams',
    choices: [],         // populated from /api/orders/filter-options (depends on club selection)
    searchable: true,
    defaultHidden: false,
  },
  {
    // Distinct player names parsed from the order's _soccer_order_info
    // customAttribute. Display + global search only — never filterable.
    id: 'player_names',
    label: 'Player(s)',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'player_names',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    // Player numbers, index-aligned to player_names so each row in the
    // column stacks under its player. Empty per-player when a player has
    // no number assigned; whole column shows "—" when no player has any.
    id: 'player_numbers',
    label: 'Number(s)',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'player_numbers',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'source',
    label: 'Source',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'source',
    apiFilterKey: 'sources',
    choices: [
      { label: 'Team Portal',                  value: 'team_portal' },
      { label: 'Retail',                       value: 'retail' },
      { label: 'Add-On',                       value: 'addon' },
      { label: 'Team Portal/Retail',           value: 'tp_retail' },
      { label: 'Team Portal/Add-On',           value: 'tp_addon' },
      { label: 'Retail/Add-On',                value: 'retail_addon' },
      { label: 'Team Portal/Retail/Add-On',    value: 'tp_retail_addon' },
      { label: 'Amazon FBM',                   value: 'amazon_fbm' },   // FBM-routed orders (tag FBM-Routed), 2026-10-14
    ],
    defaultHidden: false,
  },
  {
    id: 'is_custom',
    label: 'Custom',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'custom',
    apiFilterKey: 'is_customs',
    choices: [
      { label: 'Custom only',     value: 'true' },
      { label: 'Non-custom only', value: 'false' },
    ],
    defaultHidden: false,
  },
  {
    // Total active notifications applicable to this order — sum of
    // order-scoped notifications + the club-scoped notification (if any) for
    // the order's (location, club). Sort is global (denormalized onto
    // order_cache.notifications_count, recomputed on notification CRUD).
    id: 'notifications_count',
    label: 'Notifications',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'notifications_count',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'store_status',
    label: 'Store Status',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'store_status',
    apiFilterKey: 'store_statuses',
    choices: [
      { label: 'None / Not a pickup',       value: 'none' },
      { label: 'In Transit',                value: 'in_transit' },
      { label: 'Partially Ready For Pickup',value: 'partial_ready_for_pickup' },
      { label: 'Ready For Pickup',          value: 'ready_for_pickup' },
      { label: 'Partially Picked Up',       value: 'partial_picked_up' },
      { label: 'Picked Up',                 value: 'picked_up' },
    ],
    defaultHidden: false,
  },
  {
    // Tracking numbers for this order: Shopify shipping-label tracking
    // (fo.label_tracking, backed by order_cache.tracking_json) merged with
    // Bulk Shipping Portal scans (fo.bulk_tracking, backed by
    // bulk_tracking_json) into fo.tracking — one line per number. Column id
    // stays `bulk_tracking` (saved layouts / hidden-cols reference it by id);
    // only the label changed 2026-09-09 when label tracking was added.
    // Not sortable/filterable — same shape as `notes`.
    id: 'bulk_tracking',
    label: 'Tracking',
    sortable: false,
    filterType: null,
    sticky: false,
    apiSortKey: null,
    apiFilterKey: null,
    defaultHidden: false,   // shown by default, right of Store Status (ORIGINAL_ORDER)
  },
  {
    // Delivery status/date per tracking number on this order, one line per
    // number, aligned with the Tracking column above (per-row cell content
    // stays per-number — see renderCellContent). Sort/filter operate on the
    // order-LEVEL rollup columns order_cache.delivery_status/delivery_date
    // written by database.recompute_delivery_status (2026-09-09 §6).
    id: 'delivery_date',
    label: 'Delivery Date',
    filterLabel: 'Delivery Status',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'delivery_date',
    apiFilterKey: 'delivery_statuses',
    choices: [
      { label: 'Delivered',          value: 'delivered' },
      { label: 'Out for delivery',   value: 'out_for_delivery' },
      { label: 'In transit',         value: 'in_transit' },
      { label: 'Exception / Returned', value: 'exception_returned' },
      { label: 'Awaiting UPS scan',  value: 'awaiting_scan' },
      { label: 'Not tracked',        value: 'not_tracked' },
    ],
    defaultHidden: false,
  },
  {
    id: 'crm',
    label: 'CRM',
    sortable: true,
    filterType: 'choice',
    sticky: false,
    apiSortKey: 'crm',
    apiFilterKey: 'crms',
    choices: [],         // populated from /api/orders/filter-options
    searchable: true,
    defaultHidden: false,
  },
  {
    id: 'total',
    label: 'Total',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'total',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    id: 'notes',
    label: 'Notes',
    sortable: false,
    filterType: null,
    sticky: false,
    apiSortKey: null,
    apiFilterKey: null,
    defaultHidden: true,
  },
  {
    id: 'tags',
    label: 'Tags',
    sortable: false,
    filterType: null,
    sticky: false,
    apiSortKey: null,
    apiFilterKey: null,
    defaultHidden: true,
  },
  {
    id: 'reworked',
    label: 'Outstanding Reworks',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'reworked',
    apiFilterKey: null,
    defaultHidden: false,
  },
  {
    // Cumulative count of pack_fail events on the order — matches the QC
    // report. Unlike `reworked` (outstanding-only), this only grows: an order
    // that was failed once and later re-passed still counts here. Populated
    // by recompute_portal_pct via COUNT(*) on events.event_type='pack_fail'.
    id: 'total_reworks',
    label: 'Total Reworks',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'total_reworks',
    apiFilterKey: null,
    defaultHidden: false,
  },
  // Money columns from Shopify per-order totals. Default-hidden; last 3 in the
  // canonical order so they slot to the right of the table when toggled on.
  {
    id: 'net_sales',
    label: 'Net Sales',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'net_sales',
    apiFilterKey: null,
    defaultHidden: true,
    tooltip: "Shopify's subtotal: line items minus discounts, before shipping and tax.",
  },
  {
    id: 'shipping',
    label: 'Shipping',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'shipping',
    apiFilterKey: null,
    defaultHidden: true,
  },
  {
    id: 'tax',
    label: 'Tax',
    sortable: true,
    filterType: null,
    sticky: false,
    apiSortKey: 'tax',
    apiFilterKey: null,
    defaultHidden: true,
  },
  {
    // Filter-only column — there is no SKU cell in the table (orders carry
    // many SKUs, sometimes hundreds). `filterOnly: true` keeps this out of
    // the Columns picker and the table itself; AdvancedFilters still picks
    // it up via filterType so users can still filter orders by SKU.
    id: 'sku',
    label: 'SKU',
    sortable: false,
    filterType: 'text',
    sticky: false,
    apiSortKey: null,
    apiFilterKey: 'sku',
    filterOnly: true,
    defaultHidden: true,
  },
  {
    // Filter-only boolean toggle. When on, backend filters to orders whose
    // non-cancelled cache rows sit at 2+ DISTINCT locations whose names
    // start with "Teams " (see database.py::_cache_conditions,
    // split_team_fulfillment predicate). Renders as a checkbox in the
    // AdvancedFilters editor (see filterType='boolean' branch).
    // `filterOnly: true` keeps this out of the table + Columns picker.
    id: 'split_team_fulfillment',
    label: 'Split Team Fulfillment',
    sortable: false,
    filterType: 'boolean',
    sticky: false,
    apiSortKey: null,
    apiFilterKey: 'split_team_fulfillment',
    filterOnly: true,
    defaultHidden: true,
    // Copy shown alongside the checkbox in the editor pane.
    booleanCheckboxLabel: 'Show Team Portal orders where the fulfillment is split between two locations',
  },
];

export const COLUMN_MAP = Object.fromEntries(COLUMN_DEFS.map(c => [c.id, c]));

// Default visual order (2026-05-02): user-confirmed canonical layout. Pick /
// Press / Pack Status sit immediately after Print Status; Items + Players +
// Reworked group together; Delivery / Store Status / CRM / Club / Team(s) /
// Location follow. pickup_store / notes / tags stay hidden by FORCE_HIDE but
// remain at the end of the order so they appear at the bottom of the Columns
// popover when toggled on.
export const ORIGINAL_ORDER = [
  'checkbox',
  'name',
  'created_at',
  'fulfill_by',
  'customer',
  'customer_email',
  'total',
  'source',
  'is_custom',
  'notifications_count',
  'status',
  'protect_status',
  'risk_level',
  'print_status',
  'pick_pct',
  'press_pct',
  'pack_pct',
  'reworked',
  'total_reworks',
  'items',
  'players',
  'delivery',
  'store_status',
  'bulk_tracking',
  'delivery_date',
  'crm',
  'club',
  'teams',
  'player_names',
  'player_numbers',
  'location',
  'pickup_store',
  'notes',
  'tags',
  // Last 3 — hidden by default, end of the table when toggled on.
  'net_sales',
  'shipping',
  'tax',
];

/** Default visible column IDs (position-ordered, excluding hidden defaults) */
export const DEFAULT_COLUMN_ORDER = (() => {
  const seen = new Set();
  const out = [];
  // Use ORIGINAL_ORDER first (so layout matches the existing /orders table),
  // then append any newly-added cols not yet in the canonical order.
  for (const id of ORIGINAL_ORDER) {
    if (COLUMN_MAP[id] && !seen.has(id)) { out.push(id); seen.add(id); }
  }
  for (const def of COLUMN_DEFS) {
    if (!seen.has(def.id)) out.push(def.id);
  }
  return out;
})();
